import React, { useState, useCallback, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useMembershipService } from 'services/memberships';
import { Membership as MembershipType } from 'types/membership';
import { ROUTES } from 'utils/constants';
import Button from 'components/Button';
import Toast from 'components/Toast';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import RollbackMembershipModal from './RollbackMembershipModal';
import styles from '../Membership.module.scss';

function formatName(m: MembershipType) {
  const parts = [m.lastName, m.firstName].filter(Boolean);
  if (m.middleName) parts.splice(1, 0, m.middleName);
  return parts.join(', ') || '—';
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
  } catch {
    return value;
  }
}

function MembershipHistory() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const membershipService = useMembershipService();
  const [versions, setVersions] = useState<MembershipType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshotToRollback, setSnapshotToRollback] =
    useState<MembershipType | null>(null);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [membershipDetailsForEmptyState, setMembershipDetailsForEmptyState] =
    useState<MembershipType | null>(null);
  const [showMembershipId, setShowMembershipId] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const loadHistory = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await membershipService.getMembershipHistory(id);
      setVersions(data);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to load membership history.';
      const messageLower = message.toLowerCase();
      const isNoHistoryError =
        messageLower.includes('no history') ||
        messageLower.includes('does not have a history') ||
        messageLower.includes('does not have history') ||
        messageLower.includes('without history');

      if (isNoHistoryError) {
        // Backend can return "does not have a history" for some IDs.
        // Fetch the membership so we can show a human-friendly name.
        try {
          const membership = await membershipService.getMembership(id);
          setMembershipDetailsForEmptyState(membership);
        } catch {
          setMembershipDetailsForEmptyState(null);
        }
        setShowMembershipId(false);
        setVersions([]);
        setError(null);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [id, membershipService]);

  useEffect(() => {
    if (id) {
      void loadHistory();
    } else {
      setVersions([]);
      setError(null);
      setMembershipDetailsForEmptyState(null);
      setShowMembershipId(false);
    }
  }, [id, loadHistory]);

  const handleRollbackClick = (snapshot: MembershipType) => {
    setSnapshotToRollback(snapshot);
  };

  const handleRollbackConfirm = useCallback(async () => {
    if (!id || !snapshotToRollback) return;
    setIsRollingBack(true);
    try {
      await membershipService.rollbackMembership(id, snapshotToRollback.id);
      setSnapshotToRollback(null);
      void loadHistory();
      setToast({
        message: 'Membership rolled back successfully',
        type: 'success',
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to rollback membership.';
      setToast({ message, type: 'error' });
    } finally {
      setIsRollingBack(false);
    }
  }, [id, snapshotToRollback, membershipService, loadHistory]);

  const isCurrentVoided = versions[0]?.isVoided ?? false;
  const emptyStateFullName = membershipDetailsForEmptyState
    ? formatName(membershipDetailsForEmptyState)
    : '';

  if (!id) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.title}>Membership history</h1>
        </header>
        <div className={styles.emptyState}>
          <p>
            View membership history from the{' '}
            <Link to={ROUTES.MEMBERSHIP.ROOT}>membership list</Link> by
            selecting a membership and clicking History.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}
          className={styles.backButton}
          aria-label="Back to list"
        >
          <ArrowLeft size={20} strokeWidth={2} />
          Back
        </Button>
        <h1 className={styles.title}>Membership history</h1>
      </header>

      {loading && (
        <div className={styles.loadingState}>
          <p>Loading history...</p>
        </div>
      )}

      {error && !loading && (
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button onClick={() => void loadHistory()}>Retry</Button>
        </div>
      )}

      {!loading && !error && versions.length === 0 && (
        <div className={styles.emptyState}>
          <p>
            There is no saved history for{' '}
            <strong>{emptyStateFullName || 'this membership'}</strong>{' '}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={styles.emptyStateIdToggle}
              onClick={() => setShowMembershipId((prev) => !prev)}
              aria-label="Show membership ID"
            >
              Click here
            </Button>
            {showMembershipId && (
              <>
                {' '}
                Membership ID: <code>{id}</code>
              </>
            )}
            .
          </p>
          <Button onClick={() => navigate(ROUTES.MEMBERSHIP.ROOT)}>
            Back to list
          </Button>
        </div>
      )}

      {!loading && !error && versions.length > 0 && (
        <div className={styles.historyList}>
          {versions.map((m, index) => (
            <div key={m.id} className={styles.historyItem}>
              <div className={styles.historyItemHeader}>
                <div>
                  <strong>{formatName(m)}</strong>
                  {index === 0 ? (
                    <span className={styles.currentBadge}>Current</span>
                  ) : (
                    <span className={styles.historyBadge}>Snapshot</span>
                  )}
                </div>
                <div className={styles.historyItemMeta}>
                  <span>{formatDate(m.updatedAt)}</span>
                  {index > 0 && !isCurrentVoided && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRollbackClick(m)}
                      className={styles.rollbackButton}
                    >
                      <RotateCcw size={16} />
                      Rollback
                    </Button>
                  )}
                </div>
              </div>
              <div className={styles.historyItemDetails}>
                {m.email && (
                  <div>
                    <strong>Email:</strong> {m.email}
                  </div>
                )}
                <div>
                  <strong>Status:</strong>{' '}
                  <span
                    className={
                      m.isVoided ? styles.voidedBadge : styles.activeBadge
                    }
                  >
                    {m.isVoided ? 'deleted' : m.status || 'active'}
                  </span>
                </div>
                {m.membershipType && (
                  <div>
                    <strong>Membership type:</strong> {m.membershipType}
                  </div>
                )}
                {m.membershipDate && (
                  <div>
                    <strong>Membership date:</strong>{' '}
                    {formatDate(m.membershipDate)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <RollbackMembershipModal
        show={snapshotToRollback !== null}
        onHide={() => setSnapshotToRollback(null)}
        snapshot={snapshotToRollback}
        onConfirm={() => void handleRollbackConfirm()}
        isSubmitting={isRollingBack}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default MembershipHistory;
